import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DownloadIcon from '@mui/icons-material/Download'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { useSnackbar } from 'notistack'
import { SecaoProduto } from '@/components/produtos/SecaoProduto'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  informativoProdutoService,
  mensagemErroInformativo,
  codigoErroInformativo,
} from '@/services/informativoProdutoService'
import { useBaixarManual } from '@/hooks/useBaixarManual'
import type { InformativoProdutoDetalhe, InformativoVersao, InformativoVigente } from '@/types/informativo'

/** Texto do estado sem vigente — o mesmo da lista do consultor (Q34: "Informativo indisponível"). */
export const TEXTO_INFORMATIVO_INDISPONIVEL = 'Informativo indisponível'

const dataBr = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—')
const tamanho = (bytes?: number | null) =>
  bytes && bytes > 0 ? `${(bytes / 1024).toFixed(1)} KB` : null

/** Quem publicou, para o back-office — a carga da migração não tem autor humano. */
export const autorDaVersao = (v: InformativoVersao): string =>
  v.publicadoPorNome?.trim() || (v.origem === 'MIGRACAO' ? 'Carga inicial (migração)' : '—')

/** Situação da versão no histórico — rótulo do que o backend gravou, nada derivado de data. */
export const situacaoDaVersao = (v: InformativoVersao): string => {
  if (v.vigente ?? !v.encerradoEm) return 'Vigente'
  if (v.motivo === 'RETIRADO') return `Retirado em ${dataBr(v.encerradoEm)}`
  return `Substituído em ${dataBr(v.encerradoEm)}`
}

const ehPdf = (f: File) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')

interface Props {
  produtoId: string
  /** Persona consultor (critério `ehConsultor` do menu): só o vigente, sem versão nem autor. */
  consultor: boolean
  /** ADMIN publica e retira (Q-AN11-1 provisória). */
  podePublicar: boolean
}

/**
 * Aba "Informativo (PDF)" do detalhe do produto (Onda 5 — AN-11 §1.4, L16).
 *
 * - Consultor: o vigente com "Abrir" ou "Informativo indisponível" — sem versão, sem quem
 *   publicou (o servidor nem manda). Lê de `GET /informativos-vigentes`.
 * - Back-office (ADMIN, GERENTE, OPERADOR): cartão do vigente (versão, data, autor,
 *   observação) e o Histórico com todas as versões, cada uma baixável.
 * - ADMIN: "Publicar novo informativo" (PDF → upload `PRODUTO/MANUAL` → publicar) e "Retirar"
 *   (com motivo). Erros do servidor (422/409) aparecem na própria tela.
 */
export const AbaInformativo: FC<Props> = ({ produtoId, consultor, podePublicar }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { baixar, baixandoId, erro: erroDownload, limparErro } = useBaixarManual()

  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)
  const [vigenteConsultor, setVigenteConsultor] = useState<InformativoVigente | null>(null)
  const [detalhe, setDetalhe] = useState<InformativoProdutoDetalhe | null>(null)

  const [publicando, setPublicando] = useState(false)
  const [retirando, setRetirando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    setSemPermissao(false)
    try {
      if (consultor) {
        const mapa = await informativoProdutoService.vigentes()
        setVigenteConsultor(mapa.get(produtoId) ?? null)
      } else {
        setDetalhe(await informativoProdutoService.doProduto(produtoId))
      }
    } catch (e) {
      if ((e as { status?: number } | null)?.status === 403) setSemPermissao(true)
      else setErro(mensagemErroInformativo(e, 'Não foi possível carregar o informativo.'))
    } finally {
      setCarregando(false)
    }
  }, [consultor, produtoId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const vigente = consultor ? null : detalhe?.vigente ?? null
  const historico = detalhe?.historico ?? []

  const acoesAdmin =
    !consultor && podePublicar && !carregando && !erro && !semPermissao ? (
      <Stack direction="row" spacing={1}>
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setPublicando(true)}>
          Publicar novo informativo
        </Button>
        {vigente && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={() => setRetirando(true)}
          >
            Retirar
          </Button>
        )}
      </Stack>
    ) : undefined

  const corpo = () => {
    if (carregando) return <EstadoDeLista estado="carregando" mensagem="Carregando o informativo…" />
    if (semPermissao) return <EstadoDeLista estado="semPermissao" />
    if (erro) {
      return (
        <EstadoDeLista
          estado="erro"
          mensagem="Não foi possível carregar o informativo"
          detalhe={erro}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      )
    }

    if (consultor) {
      if (!vigenteConsultor) {
        return (
          <Alert severity="info">
            {TEXTO_INFORMATIVO_INDISPONIVEL} — a Originei ainda não publicou o PDF deste produto.
          </Alert>
        )
      }
      return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <PictureAsPdfIcon sx={{ fontSize: 56, color: 'error.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {vigenteConsultor.nome}
              </Typography>
              {tamanho(vigenteConsultor.tamanhoBytes) && (
                <Typography variant="caption" color="text.secondary">
                  {tamanho(vigenteConsultor.tamanhoBytes)}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={baixandoId !== null}
              onClick={() => void baixar(vigenteConsultor)}
            >
              {baixandoId === vigenteConsultor.documentoId ? 'Abrindo…' : 'Abrir'}
            </Button>
          </Stack>
        </Paper>
      )
    }

    return (
      <Stack spacing={3}>
        {vigente ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }} aria-label="Informativo vigente">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <PictureAsPdfIcon sx={{ fontSize: 56, color: 'error.main' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {vigente.nome}
                  </Typography>
                  <Chip size="small" color="success" label={`Vigente · versão ${vigente.versao}`} />
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  Publicado em {dataBr(vigente.publicadoEm)} por {autorDaVersao(vigente)}
                </Typography>
                {vigente.observacao && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {vigente.observacao}
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={baixandoId !== null}
                onClick={() => void baixar(vigente)}
              >
                {baixandoId === vigente.documentoId ? 'Baixando…' : 'Baixar PDF'}
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Alert severity="info">
            {TEXTO_INFORMATIVO_INDISPONIVEL} — nenhum PDF publicado. O consultor vê o produto
            sem informativo.
          </Alert>
        )}

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Histórico
          </Typography>
          {historico.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma versão publicada.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="Histórico do informativo">
                <TableHead>
                  <TableRow>
                    <TableCell>Versão</TableCell>
                    <TableCell>Arquivo</TableCell>
                    <TableCell>Publicado em</TableCell>
                    <TableCell>Por</TableCell>
                    <TableCell>Situação</TableCell>
                    <TableCell>Observação</TableCell>
                    <TableCell align="right">Arquivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historico.map((v) => (
                    <TableRow key={`${v.versao}-${v.documentoId}`}>
                      <TableCell>{v.versao}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{v.nome}</TableCell>
                      <TableCell>{dataBr(v.publicadoEm)}</TableCell>
                      <TableCell>{autorDaVersao(v)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant={v.encerradoEm ? 'outlined' : 'filled'}
                          color={v.encerradoEm ? 'default' : 'success'}
                          label={situacaoDaVersao(v)}
                        />
                      </TableCell>
                      <TableCell>
                        {v.observacao || '—'}
                        {v.observacaoEncerramento && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            Retirada: {v.observacaoEncerramento}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {v.documentoRemovido ? (
                          <Typography variant="caption" color="text.secondary">
                            Arquivo removido
                          </Typography>
                        ) : (
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          disabled={baixandoId !== null}
                          aria-label={`Baixar versão ${v.versao}`}
                          onClick={() => void baixar(v)}
                        >
                          {baixandoId === v.documentoId ? 'Baixando…' : 'Baixar'}
                        </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Stack>
    )
  }

  return (
    <SecaoProduto
      titulo="Informativo (PDF)"
      descricao={
        consultor
          ? 'PDF informativo do produto, publicado pela Originei.'
          : 'O PDF que o consultor vê na lista de produtos. Só o vigente é visível para ele; as versões anteriores ficam no histórico.'
      }
      acao={acoesAdmin}
    >
      {erroDownload && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={limparErro}>
          {erroDownload}
        </Alert>
      )}
      {corpo()}

      {podePublicar && !consultor && (
        <>
          <PublicarInformativoDialog
            aberto={publicando}
            produtoId={produtoId}
            onFechar={() => setPublicando(false)}
            onPublicado={() => {
              setPublicando(false)
              enqueueSnackbar('Informativo publicado — é o PDF que o consultor passa a ver.', {
                variant: 'success',
              })
              void carregar()
            }}
          />
          <RetirarInformativoDialog
            aberto={retirando}
            produtoId={produtoId}
            onFechar={() => setRetirando(false)}
            onRetirado={() => {
              setRetirando(false)
              enqueueSnackbar('Informativo retirado — o produto fica "Informativo indisponível".', {
                variant: 'success',
              })
              void carregar()
            }}
          />
        </>
      )}
    </SecaoProduto>
  )
}

// ─── Diálogos (ADMIN) ───

const PublicarInformativoDialog: FC<{
  aberto: boolean
  produtoId: string
  onFechar: () => void
  onPublicado: () => void
}> = ({ aberto, produtoId, onFechar, onPublicado }) => {
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [observacao, setObservacao] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  /**
   * Documento já enviado cuja publicação falhou por quarentena: "Publicar" tenta de novo com
   * ele, sem subir o arquivo outra vez (evita um documento órfão por tentativa).
   */
  const [pendente, setPendente] = useState<string | null>(null)

  useEffect(() => {
    if (!aberto) return
    setArquivo(null)
    setObservacao('')
    setErro(null)
    setPendente(null)
  }, [aberto])

  const escolher = (f: File | undefined) => {
    if (!f) return
    if (!ehPdf(f)) {
      setErro('O informativo precisa ser um arquivo PDF.')
      return
    }
    setErro(null)
    setPendente(null)
    setArquivo(f)
  }

  const publicar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      if (pendente) {
        await informativoProdutoService.publicar(produtoId, {
          documentoId: pendente,
          observacao: observacao.trim() || null,
        })
        onPublicado()
        return
      }
      if (!arquivo) return
      const r = await informativoProdutoService.enviarEPublicar(produtoId, arquivo, observacao)
      if (r.erro) {
        if (codigoErroInformativo(r.erro) === 'INFORMATIVO_EM_QUARENTENA') setPendente(r.documentoId)
        setErro(mensagemErroInformativo(r.erro, 'Não foi possível publicar o informativo.'))
        return
      }
      onPublicado()
    } catch (e) {
      if (pendente && codigoErroInformativo(e) !== 'INFORMATIVO_EM_QUARENTENA') setPendente(null)
      setErro(mensagemErroInformativo(e, 'Não foi possível enviar o PDF.'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={enviando ? undefined : onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Publicar novo informativo</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">
          O PDF publicado passa a ser o único informativo que o consultor vê. O atual (se houver)
          vai para o histórico.
        </Typography>
        {erro && <Alert severity="error">{erro}</Alert>}
        <Box>
          <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} disabled={enviando}>
            {arquivo ? 'Trocar PDF' : 'Selecionar PDF'}
            <input
              type="file"
              hidden
              accept=".pdf,application/pdf"
              aria-label="Selecionar PDF do informativo"
              onChange={(e) => {
                escolher(e.target.files?.[0])
                e.target.value = ''
              }}
            />
          </Button>
          {arquivo && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {arquivo.name}
            </Typography>
          )}
        </Box>
        <TextField
          label="Observação (opcional)"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          inputProps={{ maxLength: 500 }}
          multiline
          minRows={2}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={enviando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void publicar()} disabled={enviando || (!arquivo && !pendente)}>
          {enviando ? 'Publicando…' : pendente ? 'Tentar publicar de novo' : 'Publicar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const RetirarInformativoDialog: FC<{
  aberto: boolean
  produtoId: string
  onFechar: () => void
  onRetirado: () => void
}> = ({ aberto, produtoId, onFechar, onRetirado }) => {
  const [motivo, setMotivo] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (!aberto) return
    setMotivo('')
    setErro(null)
  }, [aberto])

  const retirar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      await informativoProdutoService.retirar(produtoId, { motivo: motivo.trim() })
      onRetirado()
    } catch (e) {
      setErro(
        (e as { status?: number } | null)?.status === 404
          ? 'Este produto já não tem informativo vigente. Recarregue a página.'
          : mensagemErroInformativo(e, 'Não foi possível retirar o informativo.'),
      )
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={enviando ? undefined : onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Retirar o informativo?</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">
          O consultor passa a ver “{TEXTO_INFORMATIVO_INDISPONIVEL}” neste produto até um novo PDF
          ser publicado. O arquivo continua no histórico.
        </Typography>
        {erro && <Alert severity="error">{erro}</Alert>}
        <TextField
          label="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={enviando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void retirar()}
          disabled={enviando || !motivo.trim()}
        >
          {enviando ? 'Retirando…' : 'Retirar informativo'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AbaInformativo
